import React, { useCallback, useRef, useState, useEffect } from 'react'
import { useDeviceScopedInit } from 'src/hooks/useDeviceScopedInit'
import Image from 'next/image'
import PropTypes from 'prop-types'

import { useTranslation } from 'src/utils/i18n'
import clsx from 'clsx'
import Grid from '@mui/material/Grid'
import ModalUnstyled from '@mui/base/ModalUnstyled'
import { styled } from '@mui/material/styles'
import { Icon } from '@iconify/react'
import { Modal as AntModal, DatePicker, Slider } from 'antd'
import PhotoBox from 'src/@core/components/photos/PhotoBox'
import { setCurrentTab } from 'src/store/apps/Common'
import { getAlbumDetail, getAlbumSummary, getGcloudAll, getLocalAndroidAll } from 'src/@api/dashboard'
import {
  LeftCircleOutlined,
  RightCircleOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import RangePickerBox from '../RangePickerBox'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPhotoData, getAlbumKey, removeNowName, resetNowName, setPhotoDateRange } from 'src/store/apps/photo'
import ContentLoading from 'src/components/ContentLoading'

const { RangePicker } = DatePicker

const BackdropUnstyled = React.forwardRef((props, ref) => {
  const { open, className, ...other } = props

  return <div className={clsx({ 'MuiBackdrop-open': open }, className)} ref={ref} {...other} />
})

BackdropUnstyled.propTypes = {
  className: PropTypes.string.isRequired,
  open: PropTypes.bool
}

const Modal = styled(ModalUnstyled)`
  position: fixed;
  width: 100%;
  z-index: 1300;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Backdrop = styled(BackdropUnstyled)`
  z-index: -1;
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  -webkit-tap-highlight-color: transparent;
`

const RegisterIllustration = styled('img')(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    height: '80%',
    width: 'calc(100vw-80px)'
  }
}))

function formateSeconds(endTime) {
  let secondTime = parseInt(endTime) //将传入的秒的值转化为Number
  let min = 0 // 初始化分
  let h = 0 // 初始化小时
  let result = ''
  if (secondTime > 60) {
    min = parseInt(secondTime / 60)
    secondTime = parseInt(secondTime % 60)
    if (min > 60) {
      h = parseInt(min / 60)
      min = parseInt(min % 60)
    }
  }
  result = `${min.toString().padStart(2, '0')}:${secondTime.toString().padStart(2, '0')}`

  return result
}

export default function Photos({ type }) {

  // 状态管理
  const dispatch = useDispatch()
  const store = useSelector(state => state.Photo)

  // 文件夹
  const [albumSummary, setAlbumSummary] = useState([])

  const photosPaseUrl = 'https://cos.spyx.com/'
  const noPhotoUrl = '/images/photos/no-photo.png'

  const getCosUrl = path => (path ? photosPaseUrl + path : noPhotoUrl)

  const getAlbumCoverUrl = item => {
    if (item.amount == 0) return noPhotoUrl
    const path = item.cover?.url || item.url || item.thumb?.url

    return getCosUrl(path)
  }

  // 目录层级
  const [level, setLevel] = useState(0)

  // 所有选中数据
  const [allData, setAllData] = useState([])

  // 选中数据
  const [selectedData, setSelectedData] = useState([])

  // 日期筛选区间（antd RangePicker 的值）
  const [antdSelectedDates, setAntdSelectedDates] = useState([])

  // 预览图片
  const [photodata, setPhotoData] = useState({})

  // 模态框
  const [open, setOpen] = React.useState(false)
  const MIN_PHOTO_ZOOM = 1
  const MAX_PHOTO_ZOOM = 5
  const PHOTO_ZOOM_STEP = 0.25
  const [photoZoom, setPhotoZoom] = useState(1)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isPhotoDragging, setIsPhotoDragging] = useState(false)
  const panDragRef = useRef({ active: false, lastX: 0, lastY: 0 })
  const photoPreviewRef = useRef(null)
  const photoStageRef = useRef(null)
  const photoZoomRef = useRef(1)

  // 只存储图片数据
  const [useImages, setImages] = useState([])
  const [isLoading, setIsLoading] = useState(true) //正在加载获取数据
  const [loadError, setLoadError] = useState(false)

  let { data: userData } = JSON.parse(localStorage.getItem('userData'))

  /** 每次请求时现读 DeviceUser，避免闭包里的旧设备 */
  const readPhotoDeviceUser = () =>
    JSON.parse(localStorage.getItem('DeviceUser') || '{}')?.username || ''

  // 设备切换时重拉相册摘要；详情分页由 Photo slice + deviceSwitched 重置
  const loadAlbumSummary = useCallback(async (_, { signal, isCurrent } = {}) => {
    dispatch(removeNowName())

    const getDeviceUser = readPhotoDeviceUser()

    setIsLoading(true)
    setLoadError(false)
    setAlbumSummary([])
    setSelectedData([])
    setAllData([])
    setLevel(0)

    const setAlbumSummaryData = async data => {
      let response

      // 相册摘要只等待 30 秒；超时后退出加载态并提供重试，避免页面长期停留在 Loading。
      const requestOpts = signal ? { signal, timeout: 30000 } : { timeout: 30000 }
      if (type === 'android-local') {
        response = await getLocalAndroidAll(data, requestOpts)
      } else if (type === 'google-cloud') {
        response = await getGcloudAll(data, requestOpts)
      } else {
        response = await getAlbumSummary(data, requestOpts)
      }

      if (isCurrent && !isCurrent()) return

      setIsLoading(false)
      if (response.data.contents != null) {
        let result = response.data.contents.map(item => {
          return {
            ...item,
            url: getAlbumCoverUrl(item)
          }
        })

        const res = result.filter(item => {
          const _name = item.album || item.name

          return !_name.includes('Videos')
        })

        setLevel(0)
        setAlbumSummary(res)
      }
    }

    try {
      if (type === 'android-local') {
        await setAlbumSummaryData({
          username: userData.email,
          params: {
            category: 'ALBUM_SUMMARY',
            deviceUdid: getDeviceUser,
            offset: 0,
            limit: 9999
          }
        })
      } else if (type === 'google-cloud') {
        await setAlbumSummaryData({
          username: getDeviceUser,
          params: {
            category: 'ALBUM_SUMMARY'
          }
        })
      } else {
        await setAlbumSummaryData({
          username: getDeviceUser,
          params: {
            category: 'ALBUM_SUMMARY'
          }
        })
      }
    } catch (err) {
      if (isCurrent && !isCurrent()) return
      if (err?.code === 'ERR_CANCELED') return
      setAlbumSummary([])
      setLoadError(true)
      setIsLoading(false)
    }
  }, [type, userData.email, dispatch])

  // 设备切换时重新拉相册摘要
  useDeviceScopedInit(loadAlbumSummary)

  // 单文件夹ref
  const foldersRef = useRef(null)

  // 滚动每次查询数量
  const reqNum = 50

  // 存放滚动百分比
  const [scroll, setScroll] = useState(0)

  // 存放当前相册文件夹信息
  const [rowData, setrowData] = useState(null)

  // 监听进度条滚动
  useEffect(() => {
    const handleScroll = () => {
      if (foldersRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = foldersRef.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }

    const currentRef = foldersRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    }
  }, [level])

  // 监听滚动百分比查询下一页数据
  useEffect(() => {
    if (scroll >= 100) {
      const row = rowData

      // 查询下一页数据
      if (!store.isLoading) {
        dispatch(
          fetchPhotoData({
            type,
            row,
            reqNum,
            getDeviceUser: readPhotoDeviceUser(),
            userData
          })
        )
      }
    }
  }, [scroll])

  // 客户端兜底筛选：接口已按日期过滤时是空操作；若接口不支持时间参数，也能筛已加载的数据
  const filterByRange = list => {
    if (!antdSelectedDates || antdSelectedDates.length !== 2) return list

    const startTime = dayjs(antdSelectedDates[0]).startOf('day').valueOf()
    const endTime = dayjs(antdSelectedDates[1]).endOf('day').valueOf()

    return list.filter(item => {
      let itemTime = NaN
      if (item.created) itemTime = dayjs(item.created).valueOf()
      else if (item.date) itemTime = Number(item.date) * 1000

      return itemTime >= startTime && itemTime <= endTime
    })
  }

  // 状态变化时更新数据
  useEffect(() => {
    setLevel(store.level)
    if (store.selectedData.length > 0 && store.allData.length > 0) {
      const data = store.selectedData.find(item => item.name === store.nowName)
      const allData = store.allData.find(item => item.name === store.nowName)
      const imgs = store.useImages.find(item => item.name === store.nowName)

      setSelectedData(filterByRange(data ? data.list : []))
      setAllData(filterByRange(allData ? allData.list : []))
      setImages(filterByRange(imgs ? imgs.list : []))
    }
  }, [store, antdSelectedDates])

  // 打开模态框
  const handleOpen = () => {
    photoZoomRef.current = 1
    setPhotoZoom(1)
    setPanOffset({ x: 0, y: 0 })
    setOpen(true)
  }

  // 关闭
  const handleClose = () => {
    setOpen(false)
    photoZoomRef.current = 1
    setPhotoZoom(1)
    setPanOffset({ x: 0, y: 0 })
  }

  const openSetPhotoData = result => {
    if (JSON.stringify(result.medium) != '{}') {
      setPhotoData({ id: result.id, ...result.medium, ...result })
    } else {
      setPhotoData({ id: result.id, ...result.original, ...result })
    }
  }

  const resetPhotoView = () => {
    photoZoomRef.current = 1
    setPhotoZoom(1)
    setPanOffset({ x: 0, y: 0 })
    setIsPhotoDragging(false)
  }

  // 上一个图片
  const previousPhotos = () => {
    let index = useImages.findIndex(item => {
      return item.id == photodata.id
    })

    let result = useImages[index - 1]

    if (typeof result === 'undefined') return
    resetPhotoView()
    if (JSON.stringify(result.medium) != '{}') {
      setPhotoData({ id: result.id, ...result.medium, ...result })
    } else {
      setPhotoData({ id: result.id, ...result.original, ...result })
    }
  }

  //  下一个图片
  const nextPhotos = () => {
    let index = useImages.findIndex(item => {
      return item.id == photodata.id
    })
    let result = useImages[index + 1]

    if (typeof result === 'undefined') return
    resetPhotoView()
    if (JSON.stringify(result.medium) != '{}') {
      setPhotoData({ id: result.id, ...result.medium, ...result })
    } else {
      setPhotoData({ id: result.id, ...result.original, ...result })
    }
  }

  const clampPhotoZoom = value => Math.min(MAX_PHOTO_ZOOM, Math.max(MIN_PHOTO_ZOOM, Number(value.toFixed(2))))

  const clampPanOffset = (offset, zoom = photoZoomRef.current) => {
    const stage = photoStageRef.current
    const img = photoPreviewRef.current
    if (!stage || !img || zoom <= 1) return { x: 0, y: 0 }

    const baseW = img.offsetWidth
    const baseH = img.offsetHeight
    if (!baseW || !baseH) return { x: 0, y: 0 }

    const maxX = Math.max(0, (baseW * zoom - stage.clientWidth) / 2)
    const maxY = Math.max(0, (baseH * zoom - stage.clientHeight) / 2)

    return {
      x: Math.min(maxX, Math.max(-maxX, offset.x)),
      y: Math.min(maxY, Math.max(-maxY, offset.y))
    }
  }

  const applyPhotoZoom = nextZoom => {
    const zoom = clampPhotoZoom(nextZoom)
    photoZoomRef.current = zoom
    setPhotoZoom(zoom)
    setPanOffset(prev => clampPanOffset(prev, zoom))
  }

  const zoomInPhoto = e => {
    e.stopPropagation()
    applyPhotoZoom(photoZoomRef.current + PHOTO_ZOOM_STEP)
  }

  const zoomOutPhoto = e => {
    e.stopPropagation()
    applyPhotoZoom(photoZoomRef.current - PHOTO_ZOOM_STEP)
  }

  const onPhotoZoomSliderChange = value => {
    applyPhotoZoom(value / 100)
  }

  const onPhotoPointerDown = e => {
    if (photoZoomRef.current <= 1) return
    e.stopPropagation()
    panDragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY }
    setIsPhotoDragging(true)
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPhotoPointerMove = e => {
    if (!panDragRef.current.active) return
    e.stopPropagation()
    const dx = e.clientX - panDragRef.current.lastX
    const dy = e.clientY - panDragRef.current.lastY
    panDragRef.current.lastX = e.clientX
    panDragRef.current.lastY = e.clientY
    setPanOffset(prev => clampPanOffset({ x: prev.x + dx, y: prev.y + dy }, photoZoomRef.current))
  }

  const onPhotoPointerUp = e => {
    if (!panDragRef.current.active) return
    panDragRef.current.active = false
    setIsPhotoDragging(false)
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }

  useEffect(() => {
    photoZoomRef.current = photoZoom
  }, [photoZoom])

  useEffect(() => {
    const stage = photoStageRef.current
    if (!open || !stage) return undefined

    const onWheel = event => {
      event.preventDefault()
      event.stopPropagation()
      const delta = event.deltaY > 0 ? -0.15 : 0.15
      applyPhotoZoom(photoZoomRef.current + delta)
    }

    const onResize = () => {
      setPanOffset(prev => clampPanOffset(prev, photoZoomRef.current))
    }

    stage.addEventListener('wheel', onWheel, { passive: false })
    window.addEventListener('resize', onResize)

    return () => {
      stage.removeEventListener('wheel', onWheel)
      window.removeEventListener('resize', onResize)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    setPanOffset(prev => clampPanOffset(prev, photoZoomRef.current))
  }, [open, photodata.id, photoZoom])

  // 返回目录
  const goBack = () => {
    // 清空上次打开文件名名称
    dispatch(removeNowName())
  }

  const [currentVideo, setCurrentVideo] = useState({})

  // 播放视频模态框
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  // 关闭视频模态框
  const cancelVideoModalOpen = () => {
    setVideoModalOpen(false)
    setCurrentVideo({})
  }

  // 当前视频对象
  const playVideo = row => {
    setVideoModalOpen(true)
    setCurrentVideo(row)
  }

  //选择日期范围：交给接口按日期重新取数（列表渲染仍会做一次客户端兜底筛选）
  const handleDateChange = dates => {
    const picked = dates && dates.length === 2 ? dates : []
    setAntdSelectedDates(picked)

    dispatch(setPhotoDateRange(picked.length === 2 ? picked : null))

    const row = rowData
    if (row) {
      if (foldersRef.current) foldersRef.current.scrollTop = 0
      setScroll(0)
      dispatch(
        fetchPhotoData({
          type,
          row,
          reqNum,
          getDeviceUser: readPhotoDeviceUser(),
          userData
        })
      )
    }
  }

  const { t } = useTranslation('common')
  useEffect(() => {
    const title = t('Photo')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  return (
    <>
      {level == 0 ? (
        <div className='bg-[#FFF] drop-shadow-[0_0_12px_rgba(0,0,0,0.13)] rounded-[10px] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] Bxl:h-[calc(var(--vh2)*100-90px)] pt-[20px] px-[15px] pb-[21px] overflow-auto relative'>
          {isLoading ? (
            <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
              <ContentLoading />
            </div>
          ) : loadError ? (
            <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px] flex flex-col items-center justify-center gap-[12px]'>
              <p className='text-[14px] text-[#626262]'>Photos could not be loaded.</p>
              <button
                type='button'
                className='px-[20px] py-[8px] rounded-[18px] bg-[#12D8AB] text-[#07273D] font-[600]'
                onClick={() => loadAlbumSummary()}
              >
                Try again
              </button>
            </div>
          ) : null}
          <Grid>
            <Grid container spacing={1.5}>
              {albumSummary.map(row => (
                <Grid item xs={4} md={3} lg={2} key={getAlbumKey(row)}>
                  <div
                    className='flex flex-col justify-center items-center cursor-pointer'
                    onClick={() => {
                      // 存储当前点击文件夹信息
                      setrowData(row)

                      const albumKey = getAlbumKey(row)
                      const nowData = Array.from(store.allData).find(item => item.name === albumKey)

                      // 每次点击文件夹时，未查询过数据或数据为空再发送请求
                      if (nowData === undefined || nowData.list.length === 0) {
                        dispatch(
                          fetchPhotoData({
                            type,
                            row,
                            reqNum,
                            getDeviceUser: readPhotoDeviceUser(),
                            userData
                          })
                        )
                      } else {
                        const data = store.selectedData.find(item => item.name === nowData.name)
                        const allData = store.allData.find(item => item.name === nowData.name)
                        const imgs = store.useImages.find(item => item.name === nowData.name)

                        setSelectedData(data ? data.list : [])
                        setAllData(allData ? allData.list : [])
                        setImages(imgs ? imgs.list : [])
                        setLevel(1)

                        const fileRid = nowData.name
                        const level = 1
                        const nowName = nowData.name
                        dispatch(resetNowName({ fileRid, level, nowName }))
                      }
                    }}
                  >
                    <Image
                      src={row.url}
                      width={0}
                      height={0}
                      alt={row.album}
                      className='rounded-[4px] w-[95px] h-[95px]'
                      onError={e => {
                        e.target.src = '/images/photos/no-photo.png'
                      }}
                    />
                    <div className='w-[95px] flex justify-start text-[14px] text-[#000000] mt-[8px] break-all'>
                      {row.album || row.name}
                    </div>
                    <div className='w-[95px] flex justify-start text-[14px] text-[#8A8A8D]'>{row.amount}</div>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Grid>
          {(albumSummary.length > 0 || isLoading) ||
            <div className='text-center px-2 absolute inset-0 flex justify-center items-center pointer-events-none'>
              {t('It appears theres no data to show here yet')}<br />
              {t('If data exists it may take up to 1 hour to download and appear')}<br />
              {t('We suggest checking back in a little while')}
            </div>}
        </div >
      ) : (
        <></>
      )
      }

      {
        level == 1 ? (
          <div className='bg-[#FFF] drop-shadow-[0_0_12px_rgba(0,0,0,0.13)] rounded-[10px] Bxls:Headers5:h-[calc(var(--vh2)*100-145px)] Header5:big:h-[calc(var(--vh2)*100-210px)] bigs:h-[calc(var(--vh2)*100-270px)] Bxl:h-[calc(var(--vh2)*100-90px)] flex flex-col relative'>
            <div className='w-full h-[44px] flex justify-between items-center px-[14px]'>
              <Image
                src='/images/photos/return.svg'
                width={20}
                height={20}
                alt='return'
                onClick={goBack}
                className='cursor-pointer'
              ></Image>
              <h2 className='text-[16px] text-[#000] font-[600]'>{store.fileRid}</h2>
              <div className='w-[20px] h-[20px]'></div>
            </div>
            <div className='w-full px-[12px]'>
              <RangePickerBox
                value={antdSelectedDates}
                onChange={handleDateChange}
                onClear={() => {
                  setAntdSelectedDates([])
                  handleDateChange([])
                }}
              />
            </div>
            <div ref={foldersRef} className='flex-1 overflow-auto pb-[5px] rounded-[0_0_10px_10px] py-[12px]'>
              <div className='w-full grid gap-x-[9px] gap-y-[11px] sm:gap-x-1 grid-cols-2 Sixmds:grid-cols-4 Blds:grid-cols-6 '>
                {selectedData.map(item => {
                  if (item.type === 'mp4') {
                    if (type !== 'android-local') return null

                    return (
                      <div key={item.id}>
                        <button
                          type='button'
                          disabled={!item.original_url}
                          aria-label={`Play ${item.filename || 'video'}`}
                          className={`relative w-full aspect-square flex items-center justify-center overflow-hidden rounded-lg ${
                            item.original_url ? 'cursor-pointer' : 'cursor-default'
                          }`}
                          onClick={() => playVideo(item)}
                        >
                          <PhotoBox
                            className='max-h-full max-w-full object-contain'
                            src={getCosUrl(item.cover_url)}
                            alt={item.filename || 'video'}
                            width={300}
                            height={300}
                          />
                          {item.original_url && (
                            <Image
                              src='/images/photos/play.png'
                              width={30}
                              height={30}
                              alt=''
                              className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none'
                            />
                          )}
                          {item.cover_duration && (
                            <span className='absolute bottom-1 right-1 rounded bg-[rgba(0,0,0,0.6)] px-1 text-[12px] text-white'>
                              {item.cover_duration}
                            </span>
                          )}
                        </button>
                        <div className='w-full text-[12px] pt-[8px] pl-1 text-[#000]'>
                          {item.created && dayjs(item.created).format('YYYY/MM/DD HH:mm')}
                          {item.date && dayjs.unix(item.date).format('YYYY/MM/DD HH:mm')}
                        </div>
                      </div>
                    )
                  }

                  return (
                    <div key={item.id}>
                      <div onClick={handleOpen} className='cursor-pointer'>
                        {/* 图片 */}
                        <div className='w-full aspect-square flex items-center justify-center overflow-hidden rounded-lg'>
                          <PhotoBox
                            className='max-h-full max-w-full object-contain'
                            src={getCosUrl(item.thumb_url)}
                            onClick={() => openSetPhotoData(item)}
                            alt={item.filename}
                            width={300}
                            height={300}

                          // bro={
                          //   <div className='w-full text-[12px] pt-[8px] text-[#000]'>
                          //     {item.created && dayjs(item.created).format('YYYY/MM/DD HH:mm')}
                          //     {item.date && dayjs.unix(item.date).format('YYYY/MM/DD HH:mm')}
                          //   </div>
                          // }
                          />
                        </div>
                        <div className='w-full text-[12px] pt-[8px] pl-1 text-[#000]'>
                          {item.created && dayjs(item.created).format('YYYY/MM/DD HH:mm')}
                          {item.date && dayjs.unix(item.date).format('YYYY/MM/DD HH:mm')}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* <Modal
            aria-labelledby='unstyled-modal-title'
            aria-describedby='unstyled-modal-description'
            open={open}
            onClose={handleClose}
            slots={{ backdrop: Backdrop }}
            className=''
          >
            <div className='  rounded-sm   mx-auto'>
              <div className='flex relative w-full  items-center justify-center  shadow-2x'>
                <Image
                  width={0}
                  height={0}
                  src={'https://cos.spyx.com/' + photodata.url}
                  alt='photo'
                  className='w-[calc(100%-90px)] h-[500px]'
                />
                <div className=' flex w-full absolute justify-between'>
                  <div className='flex items-center justify-star cursor-pointer' onClick={previousPhotos}>
                    <Icon
                      icon='material-symbols:chevron-left-rounded'
                      className='flex items-center w-[50px] h-[50px]  text-white'
                    />
                  </div>
                  <div className='flex justify-end items-center cursor-pointer' onClick={nextPhotos}>
                    <Icon
                      icon='material-symbols:chevron-right-rounded'
                      className='w-[50px] h-[50px]  text-white flex'
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal> */}

            {open && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className='absolute inset-0 z-[1] overflow-hidden select-none bg-[rgba(0,0,0,0.5)]'
                onClick={handleClose}
              >
                <CloseOutlined
                  className='absolute right-4 top-4 z-[5] cursor-pointer text-[28px] text-[#FFF] pointer-events-auto smj:right-3 smj:top-3 smj:text-[22px]'
                  onClick={e => {
                    e.stopPropagation()
                    handleClose()
                  }}
                />
                <LeftCircleOutlined
                  className='absolute left-2 top-1/2 z-[5] -translate-y-1/2 cursor-pointer text-[40px] text-[#FFF] pointer-events-auto smj:left-1 smj:text-[32px]'
                  onClick={e => {
                    e.stopPropagation()
                    previousPhotos()
                  }}
                />
                <RightCircleOutlined
                  className='absolute right-2 top-1/2 z-[5] -translate-y-1/2 cursor-pointer text-[40px] text-[#FFF] pointer-events-auto smj:right-1 smj:text-[32px]'
                  onClick={e => {
                    e.stopPropagation()
                    nextPhotos()
                  }}
                />
                <div
                  className='absolute bottom-4 left-1/2 z-[5] flex w-[min(360px,calc(100%-32px))] -translate-x-1/2 items-center gap-3 rounded-[24px] bg-[rgba(0,0,0,0.55)] px-4 py-2 pointer-events-auto'
                  onClick={e => e.stopPropagation()}
                >
                  <MinusCircleOutlined
                    className={`shrink-0 text-[24px] text-[#FFF] ${
                      photoZoom <= MIN_PHOTO_ZOOM ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                    }`}
                    onClick={zoomOutPhoto}
                  />
                  <Slider
                    className='photo-preview-zoom-slider mx-1 min-w-0 flex-1'
                    min={Math.round(MIN_PHOTO_ZOOM * 100)}
                    max={Math.round(MAX_PHOTO_ZOOM * 100)}
                    step={5}
                    value={Math.round(photoZoom * 100)}
                    tooltip={{ formatter: value => `${value}%` }}
                    onChange={onPhotoZoomSliderChange}
                    styles={{
                      rail: { backgroundColor: 'rgba(255,255,255,0.25)' },
                      track: { backgroundColor: '#FFCF55' },
                      handle: { borderColor: '#FFCF55', backgroundColor: '#FFF' }
                    }}
                  />
                  <span className='w-[44px] shrink-0 text-center text-[13px] text-white'>
                    {Math.round(photoZoom * 100)}%
                  </span>
                  <PlusCircleOutlined
                    className={`shrink-0 text-[24px] text-[#FFF] ${
                      photoZoom >= MAX_PHOTO_ZOOM ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                    }`}
                    onClick={zoomInPhoto}
                  />
                </div>
                <div ref={photoStageRef} className='absolute inset-0 z-[1] overflow-hidden pointer-events-none'>
                  <div className='flex h-full w-full items-center justify-center px-12 pb-20 pt-10 pointer-events-none smj:px-10 smj:pb-16 smj:pt-8'>
                    <img
                      key={photodata.id || photodata.url}
                      ref={photoPreviewRef}
                      src={getCosUrl(photodata.url)}
                      alt='img'
                      draggable={false}
                      className={`pointer-events-auto max-h-full max-w-full select-none object-contain ${
                        photoZoom > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-out'
                      }`}
                      style={{
                        WebkitUserDrag: 'none',
                        userSelect: 'none',
                        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${photoZoom})`,
                        transformOrigin: 'center center',
                        transition: isPhotoDragging ? 'none' : 'transform 120ms ease-out'
                      }}
                      onDragStart={e => e.preventDefault()}
                      onLoad={() => {
                        setPanOffset(prev => clampPanOffset(prev, photoZoomRef.current))
                      }}
                      onPointerDown={onPhotoPointerDown}
                      onPointerMove={onPhotoPointerMove}
                      onPointerUp={onPhotoPointerUp}
                      onPointerCancel={onPhotoPointerUp}
                      onClick={e => {
                        e.stopPropagation()
                        if (photoZoom <= 1) handleClose()
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {videoModalOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className='absolute w-full h-full bg-[rgba(0,0,0,0.5)] flex justify-center items-center flex-col'
                onClick={() => {
                  cancelVideoModalOpen()
                }}
              >
                <video
                  controls={true}
                  autoPlay={true}
                  id='video'
                  preload='auto'
                  loop={false}
                  muted={false}
                  className='max-w-[80%] max-h-[80%]'
                  src={getCosUrl(currentVideo.original_url)}
                  onClick={e => e.stopPropagation()}
                ></video>
              </motion.div>
            )}

            {/* <AntModal
            title={currentVideo.filename}
            open={videoModalOpen}
            onCancel={cancelVideoModalOpen}
            footer={[]}
            closable={false}
          >
            <video
              controls={true}
              autoPlay={true}
              id='video'
              preload='auto'
              loop={false}
              muted={false}
              style={{ width: '100%', height: '100%' }}
              src={photosPaseUrl + currentVideo.original_url}
            ></video>
          </AntModal> */}
          </div>
        ) : null
      }
    </>
  )
}


